// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { ActiveState, Jsonld, SeoProps } from "@mcmc/App/Widgets/SearchEngineOptimization/SearchEngineOptimization";

/**
 * Activate structure based on condition
 *
 * @param SeoProps
 * @returns ActiveState
 */
const activate = ({ state, fields, currentPage, payload }: SeoProps): ActiveState => {
    const allowedPages =
        currentPage === "Category Details" || currentPage === "Product List" || currentPage === "Categories";

    payload.currentPage = currentPage;
    if (allowedPages && currentPage !== "Categories") {
        const location = getLocation(state);
        const categoryPath =
            getSelectedCategoryPath(state) ||
            (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
        const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
        payload.category = getCategoryState(
            state,
            catalogPageState.value?.categoryIdWithBrandId ?? catalogPageState.value?.categoryId,
        )?.value;
        return allowedPages && payload?.category?.subCategoryIds?.length;
    }
    const allCategories = [] as any;
    const categoriesDataView = state.data.categories.parentCategoryIdToChildrenIds;
    for (const [key, value] of Object.entries(categoriesDataView!)) {
        if (key === emptyGuid) {
            categoriesDataView[key]!.forEach(categoryId => {
                const category = getCategoryState(state, categoryId).value;
                allCategories.push(category);
            });
        }
    }

    payload.categories = allCategories;
    return allowedPages && payload?.categories?.length;
};

interface ItemList {
    mainEntity?: any;
}

/**
 * Build the structured data
 *
 * @param SeoProps
 * @returns Jsonld
 */
const json = ({ state, url, payload }: SeoProps): Jsonld => {
    let subCategories;
    if (payload.currentPage !== "Categories") {
        subCategories = payload?.category?.subCategoryIds?.map((categoryId: any) => {
            return getCategoryState(state, categoryId).value;
        });
    } else {
        subCategories = payload.categories;
    }

    const mainEntity = [] as any;

    subCategories.forEach((item: any) => {
        const listItem: any = {
            "@type": "ItemList",
            name: item.name || item.shortDescription,
            image: item.smallImagePath,
            description: item.htmlContent?.replace(/(<([^>]+)>)/gi, "") || item.shortDescription,
            url: url + item.path,
        };

        if (item.subCategoryIds?.length) {
            listItem.itemListElement = [] as any;
            item.subCategoryIds.forEach((subCat: any) => {
                const category = getCategoryState(state, subCat).value;
                listItem.itemListElement.push({
                    "@type": "Thing",
                    name: category?.name || category?.shortDescription,
                    image: category?.smallImagePath,
                    description: category?.htmlContent?.replace(/(<([^>]+)>)/gi, "") || category?.shortDescription,
                    url: url + category?.path,
                });
            });
        }
        mainEntity.push(listItem);
    });

    if (payload.currentPage !== "Categories") {
        return {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: payload.category.shortDescription,
            description:
                payload.category?.htmlContent?.replace(/(<([^>]+)>)/gi, "") || payload.category?.shortDescription,
            url: url + payload.category.path,
            mainEntity,
        };
    }
    return {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: payload.currentPage,
        url: url + getLocation(state).pathname,
        mainEntity,
    };
};

export default { activate, json };
